// ** React Imports
import React, { 
    Dispatch, 
    SetStateAction, 
    useEffect, 
    useState } from 'react'

// ** MUI Imports
import {
    Typography, 
    Button, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    IconButton,
    RadioGroup,
    Radio,
    FormControlLabel,
    TextField} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// ** Util Imports
import { 
    toUTCTimeStamp, 
    validateEmail, 
    isValidDate, 
    isValidEndDate } from 'src/lib/utils/helpers'
import { ArrowRight, Close } from 'mdi-material-ui'
import { 
    ExportOptionType, 
    ExportOptionConfigType, 
    ExportFormErrorState} from 'src/lib/types/exports'


// ** Child Imports
import InputTextField from '../form-elements/InputTextField'
import { DatePicker } from '@mui/x-date-pickers';
import { exportReport, syncDiscount } from 'src/lib/core/api-methods';
import Alerts from '../feedback-and-dialog/Alerts';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import { errorMappings } from 'src/lib/utils/validations/discountsValidations';
import TextFieldError from '../form-elements/TextFieldError';
import { defaultErrorState } from 'src/assets/DiscountsConfig';
import { useLocale } from 'src/lib/hooks/useLocal';
import { alertSeverites } from 'src/lib/utils/constants';
import { useMerchant } from 'src/lib/contexts/MerchantContext';
import { LoadingButton } from '@mui/lab';
import FloGenericDialog from '../feedback-and-dialog/GenericDialog';

const initialFormErrorState: ExportFormErrorState = {
    startDate: {
        status: false,
        errorCode: ""
    },
    endDate: {
        status: false,
        errorCode: ""
    },
    email: {
        status: false,
        errorCode: ""
    },
}

interface FormDialogProps {
    isOpen: boolean
    setIsDialogOpen: Dispatch<SetStateAction<boolean>>
    titleText: string
    cancelButtonColor: any
    jobButtonColor: any
    cancelButtonText: string
    jobButtonText: string
    underInputText: string
    emailInputLabel: string
    emailInputPlaceholder: string
    exportCategoryData: ExportOptionConfigType[]
    startDateLabel?: string
    endDateLabel?: string
    globalEndDate?: string | Date
    globalStartDate?: string | Date
    hideRadioButtons: boolean
    hideDateRange: boolean
    callGetDiscounts?: () => void
}

const FormDialog: React.FC<FormDialogProps> = ({ 
    isOpen, 
    setIsDialogOpen, 
    titleText,
    jobButtonColor,
    jobButtonText,
    cancelButtonColor,
    cancelButtonText,
    underInputText,
    emailInputLabel,
    emailInputPlaceholder,
    exportCategoryData,
    startDateLabel,
    endDateLabel,
    globalEndDate,
    globalStartDate,
    hideRadioButtons,
    hideDateRange,
    callGetDiscounts
}) => {

    const { t } =  useLocale();
    const { merchant } = useMerchant();

    const [ openAlert, setOpenAlert ] = useState<string>('');
    const [ alertMessage, setAlertMessage ] = useState<string>('');
    const [ exportOption, setExportOption ] = useState<ExportOptionType>(exportCategoryData?.[0]?.valueType);  
    const [ inputText, setInputText ] = useState<string>("");
    const [ startDate, setStartDate ] = useState<string | Date>(new Date());
    const [ endDate, setEndDate ] = useState<string | Date>(new Date());
    const [ formErrors, setFormErrors ] = useState<ExportFormErrorState>(initialFormErrorState);
    const [ isLoading, setIsLoading ] = useState<boolean>(false);

    const [ isExportFeedbackOpen, setIsExportFeedbackOpen ] = useState<boolean>(false);

    const renderExportFeedbackContent = () => {
        return (
            <div className='flex flex-col gap-6 text-sm font-normal text-coal-dark'>
                <p>{`An email with the download link will be sent to ${inputText} once the export is ready.`}</p>
                <p>Please also check your spam or junk folders and mark as safe to continue receiving these exports in your inbox.</p>
            </div>
        )
    }


    useEffect(() => {
        typeof globalEndDate !== "undefined" && setEndDate(globalEndDate);
    }, [globalEndDate]);

    useEffect(() => {
        typeof globalStartDate !== "undefined" && setStartDate(globalStartDate);
    }, [globalStartDate]);

    const isValidForm = () => {

        let errorFound = false;

        //Input Field: Start Date
        if (!isValidDate(startDate)) {
            setFormErrors((oldValues: ExportFormErrorState) => ({ ...oldValues, ...{ startDate: { status: true, errorCode: "DC_FORM_017" } } })); 
            errorFound = true;
        }
        //Input Field: End Date
        if (!isValidDate(endDate)) {
            setFormErrors((oldValues: ExportFormErrorState) => ({ ...oldValues, ...{ endDate: { status: true, errorCode: "DC_FORM_018" } } })); 
            errorFound = true;
        }
        
        //Validation for End Date > Start Date
        if (isValidDate(endDate) &&  isValidDate(startDate) && !isValidEndDate(new Date(endDate), new Date(startDate))) {
            setFormErrors((oldValues: ExportFormErrorState) => ({ ...oldValues, ...{ endDate: { status: true, errorCode: "DC_FORM_019" } } })); 
            errorFound = true;
        } 
        if(!validateEmail(inputText)) {
            setFormErrors((oldValues: ExportFormErrorState) => ({ ...oldValues, ...{ email: { status: true, errorCode: "DC_FORM_024" } } })); 
            errorFound = true;
        }
        
        return !errorFound;
    }

    const handleSync = async () => {
        const accountId: string | undefined = merchant?.accountId;
        if(!Boolean(inputText.length)) {
            setFormErrors((oldValues: ExportFormErrorState) => ({ ...oldValues, ...{ email: { status: true, errorCode: "DC_FORM_020" } } })); 
            return;
        }
        try {
            setIsLoading(true);
            const response: any = await syncDiscount(accountId, encodeURIComponent(inputText))
            if (response?.error) {
                setIsLoading(false);
                setOpenAlert('error')
                setAlertMessage(response?.error);
                return;
            } 
            setIsLoading(false);
            setIsDialogOpen(false);
            setOpenAlert('success')
            setAlertMessage(`Successfully imported ${inputText} from shopify.`)
            typeof callGetDiscounts !== "undefined" && callGetDiscounts();
        }
        catch (e) {
            console.error(e);
            setIsLoading(false);
            setOpenAlert('error')
            setAlertMessage("Could not import discount!")
        }
    };

    const handleExport = async () => {
        if(!isValidForm()) return;
        try {
            setIsLoading(true);
            const response: any = await exportReport(
                exportOption, 
                inputText, 
                toUTCTimeStamp(startOfDay(new Date(startDate))), 
                toUTCTimeStamp(endOfDay(addDays(new Date(endDate), -1))));
            if(response?.error) {
                setIsLoading(false);
                handleAlerts(
                    alertSeverites.SEVERITY_ERROR,
                    response?.error
                );
                return;
            } 
            setIsLoading(false);
            setIsDialogOpen(false);
            setIsExportFeedbackOpen(true);
        } catch (error) {
            setIsLoading(false);
            console.error(error)
            handleAlerts( 
                alertSeverites.SEVERITY_ERROR,
                t("alert_export_failed")
            )
        }
    }

    // ** Alert Handlers ** //

    const handleAlerts = (severity: string, message?: string) => {
        setOpenAlert(severity);
        message && setAlertMessage(message);
      }
    
    const handleCloseAlert = (_event: any, reason: any) => {
        if (reason === 'clickaway') {
          return;
        }
        handleAlerts('');
    }
    

    return (
        <>
        <Dialog  
            open={isOpen}
            maxWidth={"sm"}
            PaperProps={{
                style: { borderRadius: 16 }   
            }} 
            onClose={() => setIsDialogOpen(false)}
        >
            <DialogTitle 
                color="black"
                variant='h5' 
                sx={{ 
                    padding: '1rem', 
                    fontWeight: 600, 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "space-between" 
                }} 
            >
                {titleText}
            {/* Close Icon */}
                <IconButton
                    aria-label="close"
                    onClick={() => { setIsDialogOpen(false) }}
                >
                    <Close color={"secondary"} sx={{ transform: "scale(1.3)" }} />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 4, padding: "1.5rem 1rem" }}>
               { !Boolean(hideRadioButtons) ? <RadioGroup
                    aria-labelledby="export-control-radio-group"
                    name="exportControlRadioGroup"
                    value={exportOption}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setExportOption(event.target.value as ExportOptionType)
                    }}
                >
                    { exportCategoryData?.map((
                        item: ExportOptionConfigType,
                        index: number) => {
                        return (
                            <FormControlLabel
                                key={`${item?.valueType}_${index}`} 
                                value={item?.valueType} 
                                control={<Radio size="small" />} 
                                label={<Typography variant="body1" sx={{ fontSize: '14px' }}>{item?.label}</Typography>}
                            />
                        )
                    })}
                </RadioGroup> : 
                <>
                    <Typography variant='body2' sx={{ margin: 0 }}>{exportCategoryData?.[0]?.label}</Typography>
                    <Typography variant='body2' sx={{ margin: 0 }}>{exportCategoryData?.[0]?.secondaryLabel}</Typography>
                </>
                }

                {/* Date Inputs */}
                {!Boolean(hideDateRange) ? <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <div
                        style={{ 
                            display: "flex", 
                            alignItems: "start", 
                            justifyContent: "space-between", 
                            width: "100%",
                            gap: 16 
                        }}
                    >  
                        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography 
                                    variant='body2' 
                                sx={{ 
                                    fontWeight: 500,
                                    }}>
                                {startDateLabel}
                            </Typography>
                            <DatePicker
                                disableFuture
                                value={startDate}
                                onChange={(newValue) => {
                                    setStartDate(newValue ?? "");
                                    setFormErrors((oldValues: ExportFormErrorState) => ({ ...oldValues, ...{ startDate: defaultErrorState } }))
                                }}
                                renderInput={(params) => 
                                <TextField 
                                    {...params} 
                                    size={"small"} 
                                    error={formErrors?.startDate?.status} 
                                    onFocus={() => setFormErrors((oldValues: ExportFormErrorState) => ({ ...oldValues, ...{ startDate: defaultErrorState } }))}
                                />}
                            />
                            { formErrors?.startDate?.status ? 
                                <TextFieldError 
                                    message={errorMappings?.[formErrors?.startDate?.errorCode as 
                                        keyof typeof errorMappings]} 
                                /> 
                                :
                                <></>
                            }
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography 
                                    variant='body2' 
                                sx={{ 
                                    fontWeight: 500,
                                    }}>
                                {endDateLabel}
                            </Typography>
                            <DatePicker
                            disableFuture
                            value={addDays(new Date(endDate), -1)}
                            onChange={(newValue: any) => {
                                setEndDate(addDays(newValue, 1));
                                setFormErrors((oldValues: ExportFormErrorState) => ({ ...oldValues, ...{ endDate: defaultErrorState } }))
                            }}
                            renderInput={(params) => 
                                <TextField 
                                    {...params} 
                                    size={"small"} 
                                    error={formErrors?.endDate?.status}
                                    onFocus={() => setFormErrors((oldValues: ExportFormErrorState) => ({ ...oldValues, ...{ endDate: defaultErrorState } }))} 
                                />}
                            />
                            { formErrors?.endDate?.status ? 
                                <TextFieldError 
                                    message={errorMappings?.[formErrors?.endDate?.errorCode as 
                                        keyof typeof errorMappings]} 
                                /> 
                                :
                                <></>
                            }
                        </div>
                    </div>
                </LocalizationProvider> : <></> }

                <div 
                    style={{ 
                        display: "flex", 
                        flexDirection: "column", 
                        gap: 1 
                    }}>
                    <Typography 
                        variant='body2' 
                        sx={{ 
                            fontWeight: 500,
                        }}
                    >
                        {emailInputLabel}
                    </Typography>
                    <InputTextField
                        textFieldId='export-email-input'
                        inputPlaceholder={emailInputPlaceholder} 
                        inputType="text"
                        inputMargin={"none"}
                        isFullWidth={true}
                        errorStatus={formErrors?.email?.status}
                        errorMessage={errorMappings?.[formErrors?.email?.errorCode as 
                            keyof typeof errorMappings]}
                        isRequired={true}
                        inputValue={inputText}
                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => 
                            setInputText(event.target.value)}
                        handleFocus={() => setFormErrors((oldValues: ExportFormErrorState) => ({ ...oldValues, ...{ email: defaultErrorState } }))} 
                    />
                    <Typography 
                        variant='caption' 
                        sx={{ 
                            fontWeight: 400,
                            fontSize: '12px'
                        }}
                    >
                        {underInputText}
                    </Typography>
                </div>
            </DialogContent>
            <DialogActions 
               sx={{ 
                padding: '1.5rem',
                gap: 2,  
                display: "flex", 
                alignItems: "center", 
                justifyContent: "flex-end" 
                }} 
            >
                <Button 
                    onClick={() => setIsDialogOpen(false)} 
                    size="medium"  
                    variant="text" 
                    color={cancelButtonColor} 
                    sx={{ fontSize: 16 }}
                >
                    {cancelButtonText}
                </Button>        
                <LoadingButton 
                    id='export-generate-button'
                    onClick={jobButtonText === "Import" ? handleSync : handleExport} 
                    size="medium"
                    loadingPosition="end"
                    loading={isLoading}
                    endIcon={<ArrowRight id='export-generate-button-icon' />}
                    variant="contained" 
                    color={jobButtonColor} 
                    sx={{ fontSize: 16 }}
                >
                    {jobButtonText}
                </LoadingButton>
            </DialogActions>
        </Dialog>

        {/* Alerts */}

        <FloGenericDialog 
            isOpen={isExportFeedbackOpen}
            width={400}
            handleClose={setIsExportFeedbackOpen}
            closeButtonText={"Okay"}
            content={<>{ renderExportFeedbackContent() }</>}
            title={`Your export is being generated`}
            closeButtonVariant="outlined"
            hideCloseIcon={true}
        />

        { openAlert.length ? <Alerts 
            open = { true }
            duration = { 10000 }
            closeHandler = { handleCloseAlert } 
            severity = { openAlert } 
            message = { alertMessage }
        /> : null } 
        </>
    )
}

export default FormDialog